import { useMemo } from 'react';

import buildMemberTaskFeedItems from './buildMemberTaskFeedItems';

import useMemberTaskGroups from 'services/memberTasks/queries/useMemberTaskGroups';
import useMemberTasks from 'services/memberTasks/queries/useMemberTasks';
import { MemberTaskFeedItem } from 'modules/Tasks/types';

/**
 * The member task feed as one list of group cards + ungrouped tasks.
 *
 * Composes two parallel fetches — group summaries and ungrouped tasks (ungrouped=true keeps grouped
 * tasks out of the flat list so they don't render twice) — and merges them (see buildMemberTaskFeedItems).
 *
 * Ordered client-side (due date → priority → created_at; see buildMemberTaskFeedItems). The carousel
 * instead consumes the server-ranked /feed/ endpoint; if the Tasks page ever moves onto that feed,
 * this hook is the single swap point — keep returning `MemberTaskFeedItem[]` and consumers stay unchanged.
 */
export default function useMemberTaskFeed(location?: string, options?: { enabled?: boolean }) {
    const enabled = options?.enabled ?? true;
    const groupsQuery = useMemberTaskGroups(location, { enabled });
    const ungroupedQuery = useMemberTasks({ location, ungrouped: true }, { enabled });

    const items: MemberTaskFeedItem[] = useMemo(
        () => buildMemberTaskFeedItems(groupsQuery.data ?? [], ungroupedQuery.data ?? []),
        [groupsQuery.data, ungroupedQuery.data]
    );

    return {
        items,
        isLoading: groupsQuery.isLoading || ungroupedQuery.isLoading,
        isError: groupsQuery.isError || ungroupedQuery.isError,
        error: groupsQuery.error ?? ungroupedQuery.error,
        isSuccess: groupsQuery.isSuccess && ungroupedQuery.isSuccess,
        refetch: () => Promise.all([groupsQuery.refetch(), ungroupedQuery.refetch()]),
    };
}
